import { useMemo } from "react";
import { codeIsWindowed, codeUsesRouting } from "@/pages/pipelineOperations";
import { usePipelineCode } from "./useAiHelperProps";

// A run or a test sends the scripts and the input document to the engine,
// nothing of the pipeline around them, so it cannot show what a filter, a
// route or a window would do. Wherever the Studio offers one, it says so
// whenever the pipeline has such rules, so a passing run is not read as
// proof of them.

/** What a run of the scripts leaves out: the pipeline's window, which takes
 *  its operation rules with it, or its operation rules alone. */
export type RunSkips = "window" | "operations";

export const RUN_SKIPS: Record<RunSkips, string> = {
  window:
    "Runs the scripts on the input document only. The pipeline's window, grouping and routes are not applied here: ask the assistant to preview a window.",
  operations:
    "Runs the scripts only. The pipeline's operation rules are not applied here.",
};

export const TESTS_SKIP: Record<RunSkips, string> = {
  window:
    "Tests run the scripts on their input only. The pipeline's window, grouping and routes are not applied here.",
  operations:
    "Tests run the scripts only. The pipeline's operation rules are not applied here.",
};

/** What a run of the pipeline as it stands leaves untested, or null when
 *  it leaves nothing out. */
export function codeRunSkips(code: string): RunSkips | null {
  if (codeIsWindowed(code)) return "window";
  return codeUsesRouting(code) ? "operations" : null;
}

export function useRunSkips(): RunSkips | null {
  const { code } = usePipelineCode();
  return useMemo(() => codeRunSkips(code), [code]);
}
